import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_BASE_URL } from '@/config/api';
import { normalizeError } from '@/errors/normalize';
import { captureNetworkError } from '@/services/telemetry';
import { isFeatureEnabled } from '@/config/featureFlags';
import { useUserStore } from '@/stores/userStore';

type TokenGetter = () => string | null | undefined;

// Per-call config may carry a `token` override; it is stripped before the
// request goes out (used only to build the Authorization header).
export interface RequestConfig extends AxiosRequestConfig {
  token?: string | null;
}

// Single-flight guard: N parallel 401s must trigger only one logout/redirect
let handling401 = false;

// Base HTTP client with interceptors
class ApiClient {
  // Token resolver - set once at app startup to avoid circular imports
  static _tokenGetter: TokenGetter | null = null;

  static setTokenGetter(getter: TokenGetter): void {
    ApiClient._tokenGetter = getter;
  }

  static getToken(): string | null {
    return ApiClient._tokenGetter ? (ApiClient._tokenGetter() ?? null) : null;
  }

  baseURL: string;
  defaultConfig: AxiosRequestConfig;

  // Static helpers are also exposed on the singleton for convenience (see below).
  setTokenGetter!: typeof ApiClient.setTokenGetter;
  getToken!: typeof ApiClient.getToken;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
    this.defaultConfig = {
      withCredentials: false,
      timeout: 30000,
    };
  }

  // Create request config with auth headers
  createConfig(
    config: RequestConfig = {},
    token: string | null = null,
  ): AxiosRequestConfig {
    // Auto-resolve token from user store if not explicitly provided
    const resolvedToken = token || ApiClient.getToken();
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(config.headers as Record<string, string> | undefined),
    };

    // Add auth header if token available
    if (resolvedToken) {
      headers['Authorization'] = `Bearer ${resolvedToken}`;
    }

    // Add request ID for tracking
    if (isFeatureEnabled('attachRequestIdHeader')) {
      const requestId =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : Math.random().toString(36).slice(2);
      headers['x-request-id'] = requestId;
    }

    return {
      ...this.defaultConfig,
      ...config,
      headers,
      baseURL: config.baseURL || this.baseURL,
    };
  }

  // Handle response and errors with centralized 401 handling
  async handleResponse<T = unknown>(
    axiosCall: Promise<AxiosResponse<T>>,
  ): Promise<AxiosResponse<T>> {
    try {
      const response = await axiosCall;
      return response;
    } catch (error) {
      const normalized = normalizeError(error);

      // Capture telemetry if enabled
      if (isFeatureEnabled('enableTelemetry')) {
        captureNetworkError(normalized);
      }

      // Centralized 401 handling: clear user session and redirect to login
      if (normalized.status === 401 && !handling401) {
        handling401 = true;
        useUserStore.getState().removeUser();
        if (
          typeof window !== 'undefined' &&
          window.location.pathname !== '/auth/user' &&
          window.location.pathname !== '/auth/admin'
        ) {
          window.location.href = '/auth/user';
        } else {
          // Already on a login page — allow future 401s to clear session again
          handling401 = false;
        }
      }

      throw normalized;
    }
  }

  // HTTP Methods
  async get<T = unknown>(
    url: string,
    config: RequestConfig = {},
  ): Promise<AxiosResponse<T>> {
    return this.handleResponse<T>(
      axios.get<T>(url, this.createConfig(config, config.token)),
    );
  }

  async post<T = unknown>(
    url: string,
    data?: unknown,
    config: RequestConfig = {},
  ): Promise<AxiosResponse<T>> {
    return this.handleResponse<T>(
      axios.post<T>(url, data, this.createConfig(config, config.token)),
    );
  }

  async put<T = unknown>(
    url: string,
    data?: unknown,
    config: RequestConfig = {},
  ): Promise<AxiosResponse<T>> {
    return this.handleResponse<T>(
      axios.put<T>(url, data, this.createConfig(config, config.token)),
    );
  }

  async patch<T = unknown>(
    url: string,
    data?: unknown,
    config: RequestConfig = {},
  ): Promise<AxiosResponse<T>> {
    return this.handleResponse<T>(
      axios.patch<T>(url, data, this.createConfig(config, config.token)),
    );
  }

  async delete<T = unknown>(
    url: string,
    config: RequestConfig = {},
  ): Promise<AxiosResponse<T>> {
    return this.handleResponse<T>(
      axios.delete<T>(url, this.createConfig(config, config.token)),
    );
  }
}

// Create and export singleton instance
const apiClient = new ApiClient();

// Expose static methods on the instance for convenience
apiClient.setTokenGetter = ApiClient.setTokenGetter.bind(ApiClient);
apiClient.getToken = ApiClient.getToken.bind(ApiClient);

export default apiClient;
